import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { numberWithCommas } from 'site-modules/shared/utils/string';
import { getVehiclePhoto } from 'site-modules/shared/components/lead-form/utils/utils';
import { InventoryColors } from 'site-modules/shared/components/inventory-colors/inventory-colors';
import { InventoryPrice } from 'site-modules/shared/components/inventory-price/inventory-price';
import { DealerInfo } from 'site-modules/shared/components/lead-form/unified-lead-form/components/dealer/dealer-info';
import { CoreImg } from 'site-modules/shared/components/core-img/core-img';
import { DeliveryToZip } from 'site-modules/shared/components/delivery/delivery-to-zip';
import { showTrimName } from 'client/site-modules/shared/utils/show-trim-name';
import { getCabType } from 'site-modules/shared/utils/get-cab-type';
import { getNoVehicleImageToDisplay } from 'site-modules/shared/utils/get-no-vehicle-image-to-display';

export function InventoryCard({
  leadTarget,
  priceStrikethrough = false,
  showInventoryMakeStockImage = false,
  showBestDealPrice = false,
  showVehicleMileage = false,
  showUnknownColor = false,
  showNationwideDeliveryBadge = false,
  zipCode = '',
  stateCode = '',
  showTeaserText = true,
}) {
  const { inventory, leadAttributes } = leadTarget;

  const { vin, vehicleInfo, dealerInfo, type, prices } = inventory;

  const distance = get(leadAttributes, 'distance');
  const {
    styleInfo: { year, make, model },
  } = vehicleInfo;
  const styleInfo = get(vehicleInfo, 'styleInfo');
  const trim = get(vehicleInfo, 'styleInfo.trim', '');
  const cabType = getCabType(styleInfo);

  const vehicleLabel = `${year} ${make} ${model} ${showTrimName(make, trim) ? trim : ''} ${cabType}`.trim();

  const photoUrl = getVehiclePhoto(vehicleInfo, showInventoryMakeStockImage);
  const bodyType = get(styleInfo, 'bodyType', '');

  const exteriorColors = get(vehicleInfo, 'vehicleColors.exterior');
  const interiorColors = get(vehicleInfo, 'vehicleColors.interior');

  const bestDealPrice = get(inventory, 'computedInfo.bestDealPrice', 0);

  const vehicleMileage = get(vehicleInfo, 'mileage', 0);
  const vehicleMileageDisplay = vehicleMileage > 0 ? numberWithCommas(vehicleMileage) : 'Not Provided';

  const deliveryCode = get(inventory, 'deliveryOptions.deliveryCode');
  const noVehicleImage = getNoVehicleImageToDisplay(bodyType);

  return (
    <Fragment>
      <Row className="align-items-center">
        <Col xs={4} className="align-self-start">
          <CoreImg
            className="w-100 mb-0_5"
            image={photoUrl}
            alt={vehicleLabel}
            title={vehicleLabel}
            source="media"
            clickable={false}
            enableOnError
            onErrorImage={noVehicleImage}
          />
          <InventoryColors
            exterior={exteriorColors}
            interior={interiorColors}
            showUnknownColor={showUnknownColor}
            className="mb-0_5"
          />
          <div className="d-flex">
            <div>
              <InventoryPrice
                type={type}
                prices={prices}
                priceNameUsed="List"
                priceNameNew={showBestDealPrice ? 'Edmunds Suggested Price' : 'MSRP'}
                priceNameClasses="small text-gray d-block"
                priceClasses="text-gray-darker fw-medium d-block"
                forceStrikethrough={priceStrikethrough}
                isBestDealPriceEnabled={showBestDealPrice}
                bestDealPrice={bestDealPrice}
              />
            </div>
            {showVehicleMileage && (
              <div className="ms-1">
                <span className="small text-gray d-block">Mileage</span>
                <span className="text-gray-darker fw-medium d-block">{vehicleMileageDisplay}</span>
              </div>
            )}
          </div>
        </Col>
        <Col xs={8}>
          <div className="mb-0_25 me-0_25 text-gray-darker fw-medium">{vehicleLabel}</div>
          <div className="small text-gray mb-0_75">VIN: {vin}</div>
          <DealerInfo dealerInfo={dealerInfo} distance={distance} />
          {showTeaserText && (
            <div className="small mt-0_5">
              The price you should pay is <span className="text-primary">on the next page</span>
            </div>
          )}
          {showNationwideDeliveryBadge && (
            <DeliveryToZip deliveryCode={deliveryCode} stateCode={stateCode} zipCode={zipCode} />
          )}
        </Col>
      </Row>
    </Fragment>
  );
}

InventoryCard.propTypes = {
  leadTarget: PropTypes.shape({
    inventory: PropTypes.shape({
      dealerInfo: PropTypes.shape({}).isRequired,
      vehicleInfo: PropTypes.shape({
        photo: PropTypes.shape({
          defaultPhoto: PropTypes.shape({
            large: PropTypes.shape({
              url: PropTypes.string,
            }),
          }),
        }),
        styleInfo: PropTypes.shape({
          trim: PropTypes.string,
          year: PropTypes.number.isRequired,
          model: PropTypes.string.isRequired,
          make: PropTypes.string.isRequired,
        }).isRequired,
        vehicleColors: PropTypes.shape({
          exterior: PropTypes.shape({
            r: PropTypes.number,
            b: PropTypes.number,
            g: PropTypes.number,
          }),
          interior: PropTypes.shape({
            r: PropTypes.number,
            b: PropTypes.number,
            g: PropTypes.number,
          }),
        }),
      }).isRequired,
      type: PropTypes.string.isRequired,
      vin: PropTypes.string.isRequired,
      prices: PropTypes.shape({
        displayPrice: PropTypes.number,
      }),
    }),
    leadAttributes: PropTypes.shape({
      distance: PropTypes.number,
    }).isRequired,
  }).isRequired,
  priceStrikethrough: PropTypes.bool,
  showInventoryMakeStockImage: PropTypes.bool,
  showBestDealPrice: PropTypes.bool,
  showVehicleMileage: PropTypes.bool,
  showUnknownColor: PropTypes.bool,
  showNationwideDeliveryBadge: PropTypes.bool,
  zipCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stateCode: PropTypes.string,
  showTeaserText: PropTypes.bool,
};
